import {Button, Space, Table, Tooltip} from 'antd';
import {CardProps} from 'antd/es/card';
import {ColumnProps} from 'antd/lib/table';
import {AuthorizedUser, AuthorizedUserStatus, Store, useAuthorizedUsersQuery} from 'api/generated';
import {TableCard} from 'components/TableCard';
import {UserStatusTag} from 'components/UserStatusTag';
import {useTableSearch} from 'hooks/useTableSearch';
import React, {useState} from 'react';
import {CreateMasterAccountUser} from './CreateMasterAccountUser';
import {formatRelativeDate} from 'lib/formaters';
import {truncate} from '../../../lib/utils';
import moment from 'moment-timezone';
import {MasterAccountUserActions} from './MasterAccountUserActions';

export type StoresProps = {
  stores: Store[];
  loadingStores: boolean;
};

export type Props = {
  masterAccountId: string;
  cardProps: CardProps;
};

export const MasterAccountUsers: React.FC<Props> = ({masterAccountId, cardProps}) => {
  const [size, setSize] = useState(10);
  const [from, setFrom] = useState(0);
  const {data, loading} = useAuthorizedUsersQuery({
    variables: {accountId: masterAccountId}
  });

  const masterAccountUsers = data?.authorizedUsers.items;
  const total = data?.authorizedUsers?.items.length || 0;

  const columns: Array<ColumnProps<AuthorizedUser>> = [
    useTableSearch({
      title: 'Email',
      dataIndex: 'userEmail',
      className: 'minimized-cell',
      render(_, user) {
        return <Tooltip title={user.userEmail}>{truncate(user.userEmail, 30)}</Tooltip>;
      }
    }),
    {
      title: 'Status',
      dataIndex: 'status',
      onFilter: (value, record) => record.status.includes(value as string),
      filters: [
        {text: 'Confirmed', value: AuthorizedUserStatus.CONFIRMED},
        {text: 'Unconfirmed', value: AuthorizedUserStatus.UNCONFIRMED},
        {text: 'Password change required', value: AuthorizedUserStatus.FORCE_CHANGE_PASSWORD},
        {text: 'Compromised', value: AuthorizedUserStatus.COMPROMISED},
        {text: 'Archived', value: AuthorizedUserStatus.ARCHIVED}
      ],
      className: 'minimized-cell',
      render: (status) => <UserStatusTag user={{status, enabled: true} as any} />
    },
    {
      title: 'Created at',
      dataIndex: 'createdAt',
      defaultSortOrder: 'descend',
      className: 'minimized-cell',
      render: formatRelativeDate,
      sorter: (a, b) => {
        if (!a.createdAt && !b.createdAt) return 0;
        if (!a.createdAt) return -1;
        if (!b.createdAt) return 1;
        return moment.utc(a.createdAt).diff(moment.utc(b.createdAt));
      }
    },
    {
      title: 'Last login at',
      dataIndex: 'lastLoginAt',
      className: 'minimized-cell',
      render: formatRelativeDate,
      sorter: (a, b) => {
        if (!a.lastLoginAt && !b.lastLoginAt) return 0;
        if (!a.lastLoginAt) return -1;
        if (!b.lastLoginAt) return 1;
        return moment.utc(a.lastLoginAt).diff(moment.utc(b.lastLoginAt));
      }
    },
    {
      title: 'Actions',
      key: 'actions',
      align: 'right',
      className: 'minimized-cell',
      render: (_, user) => <MasterAccountUserActions user={user} />
    }
  ];

  return (
    <TableCard
      title="Users"
      extra={<CreateMasterAccountUser accountId={masterAccountId} />}
      {...cardProps}>
      <Table
        rowKey="email"
        scroll={{x: true}}
        dataSource={masterAccountUsers}
        columns={columns as any}
        loading={loading}
        pagination={{
          total,
          pageSize: size,
          hideOnSinglePage: true,
          current: from / size + 1,
          onShowSizeChange: (current, pageSize) => {
            setSize(pageSize);
            setFrom((current - 1) * pageSize);
          },
          onChange: (page, size = 0) => setFrom((page - 1) * size)
        }}
      />
    </TableCard>
  );
};
